import  { useEffect, useMemo, useCallback } from 'react'
import CardTable from '../../../../components/common/CardTable'
import Table from '../../../../components/common/Table/Table'
import Pagination from '../../../../components/common/Table/Pagination'
import { useNavigate } from 'react-router-dom'
import ScrollToTopBtn from '../../../../components/common/ScrollToTopBtn'
import Modal from '../../../../components/common/Modal'
import Notifications from '../../../../components/common/Notifications'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { fetchProfile } from '../../../../store/slices/profileSlice'
import {
  fetchPendingApplications,
  setPendingCurrentPage,
  updateApplicationStatus,
  fetchPastApplications,
  removePendingApplication,
} from '../../../../store/slices/Tuition/tuitionManagerPortalSlice'

// Import from helpers
import { TableColumn, TableRow } from './helpers/pendingApplicationsUtil'
import { useNoteModal, useApplicationLoading } from './helpers/hooks'
import {
  LoadingState,
  AccessDeniedState,
  NoApplicationsState,
  LoadingOverlay,
  ApproveButton,
  DenyButton,
  NoteButton,
  ViewDetailsButton,
} from './helpers/components'
import { createApplicationActionHandler } from './helpers/handlers'


// ==================== TYPES ====================

interface PendingApplicationsState {
  applications: any[]
  loading: boolean
  error: string | null
  currentPage: number
  perPage: number
  totalItems: number
}

// ==================== CONSTANTS ====================

const COLUMN_DEFINITIONS: TableColumn[] = [
  { key: 'empNuid', label: 'Employee ID' },
  { key: 'empName', label: 'Name' },
  { key: 'id', label: 'Application ID' },
  { key: 'applicationDate', label: 'Application Date' },
  { key: 'approve', label: 'Approve Application' },
  { key: 'deny', label: 'Deny Application' },
  { key: 'note', label: 'Note' },
  { key: 'action', label: 'View' },
]

const NOTE_MAX = 1000

// ==================== MAIN COMPONENT ====================

const PendingApprovalApplications = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  // ==================== REDUX STATE ====================

  const pendingState = useAppSelector(
    (state: any) => state?.tuitionManagerPortal?.pendingApplications
  )
  const { applications = [], loading = false, error = null, currentPage = 1, perPage = 10, totalItems = 0 } =
    (pendingState as PendingApplicationsState) || {}

  const profile = useAppSelector((state: any) => state.profile.profile)
  const profileLoading = useAppSelector((state: any) => state.profile.loading)
  const profileError = useAppSelector((state: any) => state.profile.profileError)
  const displayError = profileError || error

  // ==================== CUSTOM HOOKS ====================

  const {
    showNoteModal,
    noteText,
    selectedAppId,
    savedNotes,
    setNoteText,
    openNote,
    saveNote,
    closeNote,
    clearNote,
    clearNoteText,
  } = useNoteModal()

  const { actionLoadingId, setActionLoadingId } = useApplicationLoading()

  // ==================== EFFECTS ====================

  /**
   * Fetch profile and pending applications on mount and when dependencies change
   */
  useEffect(() => {
    if (!profile && !profileLoading && !profileError) {
      dispatch(fetchProfile())
      return
    }

    if (profile?.isManager) {
      dispatch(fetchPendingApplications({ page: currentPage, per_page: perPage }))
    }
  }, [currentPage, perPage, dispatch, profile, profileLoading, profileError])

  // ==================== HANDLERS ====================

  /**
   * Generic handler wrapper for application actions
   * Abstracts handler creation to eliminate duplication
   */
  const createHandler = useCallback(
    (status: 'Manager Accepted' | 'Manager Rejected') => async (row: any) => {
      const handler = createApplicationActionHandler({
        row,
        status,
        savedNotes,
        applications,
        currentPage,
        perPage,
        dispatch,
        updateApplicationStatus,
        removePendingApplication,
        fetchPendingApplications,
        fetchPastApplications,
        onClearNote: (appId: string) => {
          clearNote(appId)
        },
        onSetActionLoadingId: setActionLoadingId,
      })
      await handler()
    },
    [
      savedNotes,
      applications,
      currentPage,
      perPage,
      dispatch,
      clearNote,
      setActionLoadingId,
    ]
  )

  /**
   * Handle approve action
   */
  const handleApprove = useCallback(
    (row: any) => createHandler('Manager Accepted')(row),
    [createHandler]
  )

  /**
   * Handle deny action
   */
  const handleDeny = useCallback(
    (row: any) => createHandler('Manager Rejected')(row),
    [createHandler]
  )

  /**
   * Handle open note modal
   */
  const handleOpenNote = useCallback(
    (row: any) => {
      openNote(String(row?.id))
    },
    [openNote]
  )

  /**
   * Handle page change
   */
  const handlePageChange = useCallback(
    (page: number) => {
      dispatch(setPendingCurrentPage(page))
    },
    [dispatch]
  )

  /**
   * Handle view details navigation
   */
  const handleActionClick = useCallback(
    (row: any) => {
      const id = row?.id ?? ''
      const empNuid = row?.empNuid ?? ''
      if (id && empNuid) {
        navigate(`/tuition/manager-details/${encodeURIComponent(String(id))}`, {
          state: { empNuid },
        })
      }
    },
    [navigate]
  )

  // ==================== MEMOIZED DATA ====================

  /**
   * Memoized columns definition
   */
  const columns: TableColumn[] = useMemo(() => COLUMN_DEFINITIONS, [])

  /**
   * Memoized table rows with action buttons
   * Creates row data structure with button components
   */
  const rows: TableRow[] = useMemo(
    () =>
      (Array.isArray(applications) ? applications : []).map((r) => ({
        ...r,
        approve: (
          <ApproveButton row={r} onClick={handleApprove} disabled={!!actionLoadingId} />
        ),
        deny: (
          <DenyButton row={r} onClick={handleDeny} disabled={!!actionLoadingId} />
        ),
        note: <NoteButton row={r} onClick={handleOpenNote} />,
        action: <ViewDetailsButton />,
      })),
    [applications, actionLoadingId, handleApprove, handleDeny, handleOpenNote]
  )

  // ==================== RENDER CONTENT ====================

  /**
   * Render main content based on state
   */
  const renderContent = () => {
    // Show loader while profile or applications are loading
    if (profileLoading || loading) {
      return <LoadingState />
    }

    // Show access denied if user is not a manager
    if (profile && !profile.isManager) {
      return <AccessDeniedState />
    }

    if (displayError) {
      return (
        <div className="min-h-[70vh] flex items-center justify-center">
          <Notifications messageInfo={displayError} status={false} />
        </div>
      )
    }

    // Show no applications message if empty
    if (Array.isArray(applications) && applications.length === 0) {
      return <NoApplicationsState />
    }

    // Show table with applications
    return (
      <CardTable>
        <Table
          columns={columns}
          rows={rows}
          onActionClick={handleActionClick}
          caption="Pending approval applications"
          isSorting={false}
        />
        <div className="mt-4 mobile:relative mobile:left-[30px]">
          <Pagination
            totalItems={totalItems || (Array.isArray(applications) ? applications.length : 0)}
            itemsPerPage={perPage}
            currentPage={currentPage}
            onPageChange={handlePageChange}
          />
        </div>
      </CardTable>
    )
  }

  // ==================== MAIN RENDER ====================

  return (
    <div className="page-container">
      {/* Loading overlay during async operations */}
      {actionLoadingId && <LoadingOverlay />}

      {/* Main content - rendered based on state */}
      {renderContent()}

      {/* Note modal dialog - always in DOM */}
      <Modal
        isOpen={showNoteModal}
        title="Add note"
        onClose={closeNote}
        clearBtn="Clear information"
        submitBtn="Submit"
        onPrimary={saveNote}
        onSecondary={clearNoteText}
        primaryDisabled={noteText.length > NOTE_MAX}
      >
        <div className="form-group w-full">
          <div className="textarea-wrapper w-full relative">
            <p id="modal-note-label" className="p3 -medium">
              Note
            </p>
            <textarea
              autoComplete="on"
              id={`modal-note-${selectedAppId}`}
              name="note"
              aria-labelledby="modal-note-label modal-note-count"
              className="text-area_word-count text-area_global -book !text-base !text-[#0D1C3D] !w-full"
              data-show-lines="5"
              rows={5}
              value={noteText}
              onChange={(e) => setNoteText(e.target.value.slice(0, NOTE_MAX))}
              aria-label="note-textarea"
              maxLength={NOTE_MAX}
            />
            <div className="character-count">
              <span
                id="modal-note-count"
                aria-live="polite"
                aria-atomic="true"
                className="count-area text-input-helper"
                data-description={` of ${NOTE_MAX} characters left`}
                data-full-text={`No more characters available, ${NOTE_MAX} of ${NOTE_MAX} characters left`}
              >
                {`${NOTE_MAX >= noteText.length ? NOTE_MAX - noteText.length : 0} of ${NOTE_MAX} characters left`}
              </span>
            </div>
          </div>
        </div>
      </Modal>

      {/* Scroll to top button - ALWAYS rendered, not conditional */}
      <ScrollToTopBtn />
    </div>
  )
}

export default PendingApprovalApplications